/**
 * ExternalTherapistCard — parent-facing summary of any non-PlayFirst
 * ("therapist_lite") therapist(s) the parent has invited.
 *
 * Reads from two existing sources — no new data models:
 *   • GET /parent-app/therapist-lite-links   (active external links)
 *   • GET /parent-app/children               (current pfp_priority_goals)
 *
 * Shows one card per (child, therapist) with the therapist's current
 * weekly goals and a note that those goals influence play ideas. If the
 * therapist hasn't set goals yet we show a gentle "waiting" state so
 * the parent knows the invite worked and the goal round-trip is live.
 *
 * Reuses styling from other PFP cards (rounded-3xl, stone borders).
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Stethoscope, Sparkles, Target, Clock, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function ExternalTherapistCard() {
  const [links, setLinks] = useState([]);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [l, c] = await Promise.all([
          axios.get(`${API}/parent-app/therapist-lite-links`, auth()),
          axios.get(`${API}/parent-app/children`, auth()),
        ]);
        setLinks(l.data?.links || []);
        // /parent-app/children returns array OR { children: [...] } depending
        // on server version — normalise here.
        const raw = c.data;
        setChildren(Array.isArray(raw) ? raw : (raw?.children || []));
      } catch {
        /* silent — a missing endpoint just means no card renders */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (!links.length) return null;

  // Build one row per (link, matched child). If the child was deleted we
  // still show the therapist so the parent doesn't lose track of who's
  // connected.
  const rows = links.map((l) => {
    const child = children.find((c) => c.id === l.child_id);
    const goals = (child?.pfp_priority_goals || []).filter(Boolean);
    return {
      key: l.id,
      therapistName: l.therapist_name || l.therapist_email,
      childName: l.child_name || child?.name || 'your child',
      goals,
      goalsUpdatedAt: child?.pfp_priority_goals_updated_at || null,
      source: child?.pfp_priority_goals_source || null,
    };
  });

  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="rounded-3xl bg-white shadow-sm ring-1 ring-stone-200 p-5 md:p-6 space-y-4"
      data-testid="external-therapist-card"
    >
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#EAD7D1]/40 flex items-center justify-center flex-shrink-0">
          <Stethoscope className="w-5 h-5 text-[#7C6F9C]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-wider text-[#7C6F9C] font-medium">
            Connected therapist
          </p>
          <h3 className="font-serif text-lg md:text-xl text-[#3a2f4f] mt-0.5">
            {rows.length === 1
              ? rows[0].therapistName
              : `${rows.length} therapists connected`}
          </h3>
          <p className="text-xs text-stone-500 mt-0.5">External therapist · setting weekly goals</p>
        </div>
      </div>

      {rows.map((r) => (
        <div key={r.key} className="pl-14 space-y-2" data-testid={`external-therapist-row-${r.key}`}>
          {rows.length > 1 && (
            <p className="text-sm text-stone-700 font-medium">
              {r.therapistName} · for {r.childName}
            </p>
          )}
          {r.goals.length > 0 ? (
            <>
              <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 rounded-xl px-3 py-2">
                <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
                <span>
                  Your therapist's goals are helping personalise {r.childName}'s play ideas.
                </span>
              </div>
              <ul className="space-y-1.5">
                {r.goals.slice(0, 3).map((g, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-2 text-sm text-stone-800"
                    data-testid={`external-therapist-goal-${r.key}-${i}`}
                  >
                    <Target className="w-4 h-4 text-[#7C6F9C] mt-0.5 flex-shrink-0" />
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="flex items-start gap-2 text-xs text-stone-500 bg-stone-50 rounded-xl px-3 py-2">
              <Clock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                Waiting on {r.therapistName} to set this week's goals for {r.childName}.
                A magic-link email was sent to them; a new one goes out every Monday.
              </span>
            </div>
          )}
        </div>
      ))}
    </motion.section>
  );
}

/**
 * Companion hook so ParentAppHome can suppress the "Connect a therapist"
 * CTA when any external therapist is already connected without having
 * to fetch the same endpoint twice.
 */
export function useHasActiveExternalTherapist() {
  const [has, setHas] = useState(null); // null = loading
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/parent-app/therapist-lite-links`, auth());
        if (alive) setHas((r.data?.links || []).length > 0);
      } catch {
        if (alive) setHas(false);
      }
    })();
    return () => { alive = false; };
  }, []);
  return has;
}
